// Las tarjetas NFC de una persona, en su tarjeta de Personal · N3
//
// Qué tiene (etiqueta, desde cuándo, estado), «Dar tarjeta» de dos formas
// —tecleando el número que entrega un lector USB, o pidiéndole a una
// terminal que la lea— y «Quitar» con motivo. La baja de la persona la
// deja sin efecto sola (disparador de la 800) y aquí se ve. El número de
// la tarjeta no se guarda ni se enseña: solo una huella.
import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import { CreditCard, Nfc } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';

function fecha(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
}

export default function TarjetasDePersona({ idUsuario, personaActiva = true, puedeEditar = false }) {
  const [tarjetas, setTarjetas] = useState(null);
  const [error, setError] = useState(null);
  const [modo, setModo] = useState(null);          // 'uid' | 'terminal' | null
  useSubpantalla('modo', !!modo, () => setModo(null), { valor: modo, abrir: setModo, reemplaza: true });
  const [uid, setUid] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [terminales, setTerminales] = useState([]);
  const [terminal, setTerminal] = useState('');
  const [espera, setEspera] = useState(null);      // { hasta, terminal }
  const [msg, setMsg] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    api.get(`/presencia/credenciales/${idUsuario}`)
      .then((c) => { setTarjetas(c.filter((x) => x.tipo === 'TARJETA')); setError(null); })
      .catch((e) => setError(e.message));
  }, [idUsuario]);
  useEffect(() => { cargar(); }, [cargar]);

  // Mientras se espera la lectura en la terminal, se pregunta cada 3 s si ya llegó.
  useEffect(() => {
    if (!espera) return undefined;
    const t = setInterval(() => {
      api.get(`/presencia/credenciales/${idUsuario}`).then((c) => {
        const nuevas = c.filter((x) => x.tipo === 'TARJETA');
        if (nuevas.length > (tarjetas || []).length) { setTarjetas(nuevas); setEspera(null); setMsg('Tarjeta leída y dada de alta.'); }
        else if (new Date(espera.hasta) < new Date()) { setEspera(null); setMsg('Se acabó el tiempo sin leer ninguna tarjeta. Vuelve a pedirlo cuando la tengas en la mano.'); }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [espera, idUsuario, tarjetas]);

  async function darPorUid() {
    setOcupado(true); setError(null); setMsg(null);
    try {
      await api.post(`/presencia/credenciales/${idUsuario}`, { tipo: 'TARJETA', valor: uid.trim().toUpperCase(), etiqueta: etiqueta.trim() || 'Tarjeta NFC' });
      setUid(''); setEtiqueta(''); setModo(null); setMsg('Tarjeta dada de alta. Ya abre la terminal y ficha.'); cargar();
    } catch (e) { setError(e.message); } finally { setOcupado(false); }
  }
  async function pedirLectura() {
    setOcupado(true); setError(null); setMsg(null);
    try {
      const r = await api.post(`/nfc/leer-en-terminal/${idUsuario}`, { id_dispositivo: Number(terminal) });
      setEspera({ hasta: r.hasta, terminal: r.terminal }); setModo(null);
    } catch (e) { setError(e.message); } finally { setOcupado(false); }
  }
  async function quitar(id) {
    const motivo = window.prompt('¿Por qué se quita esta tarjeta? (se queda escrito)', 'La perdió');
    if (!motivo) return;
    setOcupado(true); setError(null);
    try { await api.post(`/presencia/credenciales/${id}/revocar`, { motivo }); setMsg('Tarjeta quitada: ya no abre nada.'); cargar(); }
    catch (e) { setError(e.message); } finally { setOcupado(false); }
  }
  function abrirTerminal() {
    setModo('terminal');
    api.get('/dispositivos/terminales').then((t) => setTerminales(t.filter((x) => x.activo !== false))).catch(() => setTerminales([]));
  }

  return (
    <section className="mt-4 border-t pt-3">
      <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><Nfc className="h-4 w-4" aria-hidden="true" /> Tarjetas NFC</h4>
      <Ayuda id="nfc.tarjetas" />
      {error && <ErrorConReintento que="No se pudo con las tarjetas" porque={error} onReintentar={cargar} />}
      {tarjetas === null ? <Cargando filas={1} /> : tarjetas.length === 0 ? (
        <p className="mt-1 text-xs text-gray-600">{personaActiva ? 'Sin tarjeta. Con una, entra a la terminal de conteo y ficha sin teléfono.' : 'Sin tarjeta.'}</p>
      ) : (
        <ul className="mt-1 space-y-1">
          {tarjetas.map((t) => (
            <li key={t.id} className={`flex items-start justify-between gap-2 rounded-md p-2 text-xs ring-1 ${t.activo ? 'bg-white ring-gray-200' : 'bg-gray-50 text-gray-600 ring-gray-200'}`}>
              <span>
                <CreditCard className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
                <span className="font-medium">{t.etiqueta || 'Tarjeta'}</span> · desde {fecha(t.alta_en)}
                {t.activo && t.congelada && <span className="ml-1 text-amber-700">· congelada unos minutos por intentos fallidos</span>}
                {!t.activo && <span className="ml-1">· quitada {fecha(t.baja_en)}{t.motivo_baja === 'BAJA_PERSONA' ? ' al dar de baja a la persona' : t.motivo_baja ? ` (${t.motivo_baja})` : ''}</span>}
              </span>
              {puedeEditar && t.activo && <button type="button" onClick={() => quitar(t.id)} disabled={ocupado} className="shrink-0 rounded border border-current px-2 py-0.5">Quitar</button>}
            </li>
          ))}
        </ul>
      )}
      {msg && <p className="mt-2 text-xs text-green-700">{msg}</p>}
      {espera && <p className="mt-2 text-xs text-amber-800">Pasa la tarjeta por la terminal «{espera.terminal}» antes de {new Date(espera.hasta).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}. Aquí se verá en cuanto la lea.</p>}
      {puedeEditar && personaActiva && !modo && !espera && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" onClick={() => setModo('uid')} className="rounded border px-2 py-1 text-xs">Dar tarjeta (teclear su número)</button>
          <button type="button" onClick={abrirTerminal} className="rounded border px-2 py-1 text-xs">Leerla en una terminal</button>
        </div>
      )}
      {modo === 'uid' && (
        <div className="mt-2 space-y-2 rounded-md bg-gray-50 p-2 text-xs">
          <label className="block">Número de la tarjeta (lo escribe el lector USB al pasarla)
            <input value={uid} onChange={(e) => setUid(e.target.value)} placeholder="04A1B2C3D4E5F6" className="mt-0.5 w-full rounded border p-1 font-mono text-xs" autoFocus />
          </label>
          <label className="block">Cómo la reconoces (opcional)
            <input value={etiqueta} onChange={(e) => setEtiqueta(e.target.value)} placeholder="Tarjeta azul" className="mt-0.5 w-full rounded border p-1 text-xs" />
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={darPorUid} disabled={ocupado || uid.trim().length < 4} aria-describedby={uid.trim().length < 4 ? 'motivo-dar-tarjeta' : undefined} className="rounded bg-gray-900 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">Dar tarjeta</button>
            <button type="button" onClick={() => setModo(null)} className="rounded border px-2 py-1 text-xs">Cancelar</button>
            {/* BOT-1 (19 sep 2026): el motivo ya estaba; ahora el botón lo enlaza y dice cuánto falta. */}
            {uid.trim().length < 4 && <span id="motivo-dar-tarjeta" role="note" className="self-center text-[10px] text-gray-600">Teclea el número de la tarjeta (al menos 4 caracteres) para poder darla.</span>}
          </div>
        </div>
      )}
      {modo === 'terminal' && (
        <div className="mt-2 space-y-2 rounded-md bg-gray-50 p-2 text-xs">
          <label className="block">Qué terminal la va a leer
            <select value={terminal} onChange={(e) => setTerminal(e.target.value)} className="mt-0.5 w-full rounded border p-1 text-xs">
              <option value="">Elige una…</option>
              {terminales.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </label>
          <p className="text-[10px] text-gray-600">Tendrás dos minutos para pasar la tarjeta por esa terminal. La primera que lea queda como suya.</p>
          <div className="flex gap-2">
            <button type="button" onClick={pedirLectura} disabled={ocupado || !terminal} aria-describedby={!terminal ? 'motivo-pedir-lectura' : undefined} className="rounded bg-gray-900 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">Pedir la lectura</button>
            <button type="button" onClick={() => setModo(null)} className="rounded border px-2 py-1 text-xs">Cancelar</button>
            {!terminal && <span id="motivo-pedir-lectura" role="note" className="self-center text-[10px] text-gray-600">Elige la terminal donde se va a pasar la tarjeta.</span>}
          </div>
        </div>
      )}
    </section>
  );
}
